import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import { Avatar, Button } from 'components/ds';
import styles from './PendingInvitesSection.module.css';

/**
 * 모임 상세 참여자 카드 내 "대기 중인 초대" 소섹션.
 * - 아직 수락 안 한 (PENDING) 피초대자 목록 표시
 * - 개별 "재촉하기" 버튼 → MEETING_NUDGE 랜덤 변형 푸시 재발송
 * - 전체 재촉도 한 번에 가능
 */

interface PendingInvitee {
    inviteId: number;
    userNo: number;
    nickname: string | null;
    invitedAt?: string;
}

interface Props {
    meetingId: string;
    /** 멤버 변경/초대 발송 직후 외부에서 갱신 트리거용 */
    reloadKey?: number;
}

// 재촉 직후 in-app 토스트 문구 — 다국어 감성 랜덤
const nudgeDoneMessages = (t: (k: string, opts?: any) => string): string[] => {
    const arr = t('pendingInvites.nudgeDoneVariants', { returnObjects: true }) as unknown;
    return Array.isArray(arr) && arr.length > 0 ? (arr as string[]) : [t('pendingInvites.nudgeDoneFallback')];
};

export const PendingInvitesSection: React.FC<Props> = ({ meetingId, reloadKey = 0 }) => {
    const { t } = useTranslation();
    const [list, setList] = useState<PendingInvitee[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [nudgingUserNo, setNudgingUserNo] = useState<number | null>(null);
    const [toast, setToast] = useState<string>('');

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res: any = await MeetingRepository.onGetPendingInvites(meetingId);
            const raw = res?.data?.data || [];
            const myUserNo = Number(sessionStorage.getItem('userNo')) || 0;
            // 본인(myUserNo) 제외 + 같은 userNo 중복 제거 (DB 에 중복 PENDING 이 쌓인 경우 1건만 표시)
            const seen = new Set<number>();
            const mapped: PendingInvitee[] = [];
            for (const r of raw) {
                const userNo = Number(r.userNo);
                if (!userNo || userNo <= 0) continue;
                if (userNo === myUserNo) continue;       // 본인에게 재촉 버튼 뜨는 것 방지
                if (seen.has(userNo)) continue;          // 같은 사람 중복 제거
                seen.add(userNo);
                mapped.push({
                    inviteId: Number(r.inviteId),
                    userNo,
                    nickname: r.nickname ? decodeNick(r.nickname) : null,
                    invitedAt: r.invitedAt,
                });
            }
            setList(mapped);
        } catch {
            setList([]);
        } finally {
            setIsLoading(false);
        }
    }, [meetingId]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const nudge = async (userNos: number[]) => {
        if (userNos.length === 0) return;
        setNudgingUserNo(userNos.length === 1 ? userNos[0] : -1);
        try {
            await MeetingRepository.onPostNudgeInvitees(meetingId, userNos);
            const variants = nudgeDoneMessages(t);
            const msg = variants[Math.floor(Math.random() * variants.length)];
            setToast(msg);
            setTimeout(() => setToast(''), 1800);
        } catch {
            setToast(t('pendingInvites.nudgeFail'));
            setTimeout(() => setToast(''), 1800);
        } finally {
            setNudgingUserNo(null);
        }
    };

    if (!isLoading && list.length === 0) return null;

    return (
        <div className={styles.wrap}>
            <div className={styles.head}>
                <span className={styles.title}>{t('pendingInvites.title')} ({list.length})</span>
                {list.length > 1 && (
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => nudge(list.map(i => i.userNo))}
                        disabled={nudgingUserNo != null}
                    >
                        {t('pendingInvites.nudgeAll')}
                    </Button>
                )}
            </div>
            <ul className={styles.list}>
                {list.map(it => (
                    <li key={it.inviteId} className={styles.row}>
                        <Avatar
                            size="sm"
                            person={{
                                userNo: it.userNo,
                                name: it.nickname || String(it.userNo),
                                attendance: 'pending',
                            }}
                            statusLabel={t('pendingInvites.waitingAccept')}
                        />
                        <span className={styles.body}>
                            <span className={styles.name}>
                                {it.nickname || t('posts.userFallback', { no: it.userNo })}
                            </span>
                            <span className={styles.meta}>{t('pendingInvites.waitingAccept')}</span>
                        </span>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => nudge([it.userNo])}
                            disabled={nudgingUserNo != null}
                        >
                            {nudgingUserNo === it.userNo ? '...' : t('pendingInvites.nudge')}
                        </Button>
                    </li>
                ))}
            </ul>
            {toast && <div className={styles.toast}>{toast}</div>}
        </div>
    );
};

const decodeNick = (raw: string): string => {
    try { return decodeURIComponent(raw); } catch { return raw; }
};

export default PendingInvitesSection;
